import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Map, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useMiniTour } from "@/components/tour/MiniTourProvider";
import { isNudgeDismissed, dismissNudge, dismissAllNudges } from "@/lib/guide-nudge-prefs";
import { SpotlightRing } from "@/components/tour/tour-engine";
import type { ExplainerVideo } from "@shared/schema";

interface GuideNudgeBannerProps {
  pageKey: string;
  /**
   * Override whether a guide exists for this page. Defaults to the host-app
   * MiniTour registry (`useMiniTour().hasGuide`). The vendor portal runs a
   * separate `TourProvider`, so it injects its own value here.
   */
  hasGuide?: boolean;
  /**
   * Override what happens when the host clicks "Guide". Defaults to starting
   * the host-app MiniTour guide for `pageKey`. The vendor portal injects its
   * own `startTour` call.
   */
  onStartGuide?: () => void;
  /**
   * Whether to look up an explainer video for this page. Defaults to `true`
   * (host app). Portals that have no explainer-video catalog pass `false` to
   * avoid a guaranteed-404 fetch on every render.
   */
  enableExplainerVideo?: boolean;
}

export function GuideNudgeBanner({
  pageKey,
  hasGuide: hasGuideProp,
  onStartGuide,
  enableExplainerVideo = true,
}: GuideNudgeBannerProps) {
  const miniTour = useMiniTour();
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [permanentChecked, setPermanentChecked] = useState(false);
  // Hides both the bubble AND the spotlight rings once the banner's anchor has
  // scrolled out of the viewport, so the fixed-position rings don't keep
  // floating over unrelated content as the user scrolls down.
  const [scrolledAway, setScrolledAway] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Track whether the banner's anchor is in view. The anchor div is always
  // rendered (it's a zero-height marker) so the observer can detect both exit
  // and re-entry; the visible content is gated on `scrolledAway` below.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setScrolledAway(!entry.isIntersecting);
      },
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Belt-and-suspenders cleanup: if React fails to unmount a ring portal during
  // a wouter navigation, sweep any orphaned ring nodes tagged for this page on
  // unmount so they can't linger on the destination page.
  useEffect(() => {
    return () => {
      try {
        document
          .querySelectorAll(`[data-spotlight-ring-group="${pageKey}"]`)
          .forEach((node) => node.remove());
      } catch {
        /* ignore */
      }
    };
  }, [pageKey]);

  const { data: video } = useQuery<ExplainerVideo | null>({
    queryKey: ["/api/explainer-videos", pageKey],
    queryFn: async () => {
      const res = await fetch(`/api/explainer-videos/${pageKey}`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: enableExplainerVideo,
    retry: false,
    staleTime: 60000,
  });

  const hasGuide = hasGuideProp ?? miniTour.hasGuide(pageKey);

  if (!hasGuide) return null;
  if (isNudgeDismissed(pageKey)) return null;
  if (sessionDismissed) return null;

  const hasVideo = Boolean(video && video.embedUrl?.trim());

  function handleStartGuide() {
    dismissNudge(pageKey);
    setSessionDismissed(true);
    if (onStartGuide) onStartGuide();
    else miniTour.startGuide(pageKey);
  }

  function handleDismissSession() {
    setSessionDismissed(true);
  }

  function handlePermanentChange(checked: boolean) {
    setPermanentChecked(checked);
    if (checked) {
      // Global opt-out: one click here silences the nudge on every page.
      dismissAllNudges();
      setSessionDismissed(true);
    }
  }

  return (
    <>
      {/* Highlight rings anchored to the real Guide and Explainer Video buttons.
          Suppressed once scrolled away so the fixed rings don't float over
          unrelated content. */}
      {!scrolledAway && (
        <>
          <SpotlightRing
            selector={`[data-testid="button-guide-${pageKey}"]`}
            groupKey={pageKey}
          />
          {hasVideo && (
            <SpotlightRing
              selector={`[data-testid="button-explainer-video-${pageKey}"]`}
              groupKey={pageKey}
            />
          )}
        </>
      )}

      <div
        ref={wrapperRef}
        className="relative h-0 overflow-visible z-20"
        data-testid={`banner-guide-nudge-${pageKey}`}
      >
        {/* Floating bubble anchored to the right, where the buttons live */}
        {!scrolledAway && (
        <div className="absolute right-0 top-2 w-72 max-w-[calc(100vw-1.5rem)]">
          {/* Bubble body */}
          <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-blue-100 dark:border-blue-900 shadow-lg rounded-2xl px-3.5 py-3">
            <div className="flex items-start gap-2.5">
              <Map className="h-4 w-4 shrink-0 text-brand mt-0.5" />
              <p className="flex-1 text-sm text-gray-700 dark:text-gray-300 leading-snug">
                Check out our{" "}
                <button
                  onClick={handleStartGuide}
                  className="font-semibold text-brand hover:underline focus:outline-none"
                  data-testid={`link-guide-nudge-start-${pageKey}`}
                >
                  Guide
                </button>
                {hasVideo && " and Explainer Video"} to learn more about this page.
              </p>
              <button
                onClick={handleDismissSession}
                className="shrink-0 p-0.5 rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 mt-0.5"
                aria-label="Dismiss"
                data-testid={`button-guide-nudge-dismiss-${pageKey}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <label className="flex items-center gap-1.5 mt-2.5 cursor-pointer select-none text-gray-400 dark:text-gray-500 text-xs pl-[26px]">
              <Checkbox
                checked={permanentChecked}
                onCheckedChange={(v) => handlePermanentChange(v === true)}
                data-testid={`checkbox-guide-nudge-permanent-${pageKey}`}
                className="h-3 w-3"
              />
              Don&apos;t show again
            </label>
          </div>
        </div>
        )}
      </div>
    </>
  );
}
